import React, { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Turnstile from "@/components/Turnstile";
import { CheckCircle2, Palette, Ruler, Layers, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CustomDesign = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") === "new" ? "new" : "modify";
  const [loading, setLoading] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [form, setForm] = useState({
    type: initialType,
    name: "",
    email: "",
    country: "",
    lot_size: "",
    bedrooms: "3",
    budget: "",
    reference_plan: "",
    details: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTsToken = useCallback((t) => setTsToken(t), []);

  React.useEffect(() => {
    document.title = "Custom Home Design — Modify Any Plan | Dream Home Engineers";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "(missing REACT_APP_BACKEND_URL)";
      // eslint-disable-next-line no-console
      // [removed console.info]
      const { data } = await api.post("/enquiries", {
        type: "custom_design",
        name: form.name,
        email: form.email,
        subject: `Custom design request (${form.type})`,
        message: form.details || `Request type: ${form.type}. Bedrooms: ${form.bedrooms}. Lot: ${form.lot_size}. Budget: ${form.budget}.`,
        form_data: {
          request_type: form.type,
          country: form.country,
          lot_size: form.lot_size,
          bedrooms: form.bedrooms,
          budget: form.budget,
          reference_plan: form.reference_plan,
        },
        turnstile_token: tsToken,
      });
      // Diagnostics stay in console for admins/devs — never surfaced to customers.
      // eslint-disable-next-line no-console
      // [removed console.info]
      toast.success(data.message || `Thank you, ${form.name.split(" ")[0]}! Your Custom Design Request has been received.`, {
        description: data.reference_no ? `Reference: ${data.reference_no}` : undefined,
        duration: 8000,
      });
      setForm({ ...form, name: "", email: "", details: "" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Custom Design form] submission FAILED:", err);
      if (err?.response?.status === 429) {
        toast.error("Too many attempts", { description: err.response?.data?.detail || "Please wait a minute and try again." });
      } else {
        toast.error("Failed to submit", { description: err.response?.data?.detail || "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="overline text-accent">Custom Design Studio</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95] tracking-tighter">
              Every home is different.<br /><span className="italic text-accent">Every plan can be too.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-md">
              Our in-house architects will modify any of our house plans — or design a brand-new home — for a fixed fee. Delivered in weeks, not months.
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: Ruler, title: "Fit to your lot", note: "Adjust to slope, orientation, setbacks, and local code." },
                { icon: Palette, title: "Personalize the layout", note: "Change bedrooms, kitchen flow, add-ons, or facade." },
                { icon: Layers, title: "Full deliverable set", note: "Permit drawings, 3D renders, BIM if requested." },
              ].map(({ icon: Icon, title, note }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 bg-primary text-primary-foreground flex items-center justify-center"><Icon size={18} /></div>
                  <div>
                    <div className="font-display text-xl leading-tight">{title}</div>
                    <div className="text-sm text-muted-foreground">{note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="border border-border p-8 lg:p-10 space-y-6" data-testid="custom-design-form">
              <div className="grid grid-cols-2 gap-3 border border-border p-1.5">
                {[
                  { key: "modify", label: "Customize existing plan" },
                  { key: "new", label: "Start new design" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => set("type", opt.key)}
                    data-testid={`type-${opt.key}`}
                    className={`py-3 text-sm font-medium transition-fast ${form.type === opt.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="overline text-muted-foreground">Full name</Label>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-2 h-12" data-testid="cd-name" />
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none mt-2 h-12" data-testid="cd-email" />
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Country</Label>
                  <Input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. USA, Canada, UK" className="rounded-none mt-2 h-12" />
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Lot size</Label>
                  <Input value={form.lot_size} onChange={(e) => set("lot_size", e.target.value)} placeholder="e.g. 60 x 120 ft" className="rounded-none mt-2 h-12" />
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Bedrooms</Label>
                  <Select value={form.bedrooms} onValueChange={(v) => set("bedrooms", v)}>
                    <SelectTrigger className="rounded-none mt-2 h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>{["1","2","3","4","5","6+"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Budget (USD)</Label>
                  <Input value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. $2,000" className="rounded-none mt-2 h-12" />
                </div>
              </div>

              {form.type === "modify" && (
                <div>
                  <Label className="overline text-muted-foreground">Reference plan (name or URL)</Label>
                  <Input value={form.reference_plan} onChange={(e) => set("reference_plan", e.target.value)} placeholder="e.g. The Meridian, or paste a link" className="rounded-none mt-2 h-12" />
                </div>
              )}

              <div>
                <Label className="overline text-muted-foreground">Tell us about your project</Label>
                <Textarea rows={5} value={form.details} onChange={(e) => set("details", e.target.value)} placeholder="Family size, lifestyle, must-haves, references you love…" className="rounded-none mt-2" data-testid="cd-details" />
              </div>

              <Turnstile onToken={onTsToken} action="custom-design" />
              <Button type="submit" disabled={loading} data-testid="cd-submit" className="rounded-none bg-accent hover:bg-accent/90 h-14 px-10 w-full sm:w-auto">
                {loading ? "Sending…" : "Request quote"} <ArrowRight size={16} className="ml-2" />
              </Button>
              <p className="text-xs text-muted-foreground">Our architects reply within 24 hours with a fixed-fee quote and estimated timeline. No obligation.</p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomDesign;
